import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { listExperts, inviteMentor, listMentorClaims, forceReleaseClaim } from '../services/adminApi';
import { formatDate } from '../utils/formatters';

// UI-facing label uses "Mentor" per the naming split; backend / Cognito group
// are still "expert" internally.
export default function ExpertsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [specialization, setSpecialization] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [releasingId, setReleasingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listExperts()
      .then((data) => setExperts(data.items || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const loadClaims = useCallback(() => {
    setClaimsLoading(true);
    listMentorClaims()
      .then((data) => setClaims(data.items || []))
      .catch(() => setClaims([]))
      .finally(() => setClaimsLoading(false));
  }, []);

  useEffect(() => { load(); loadClaims(); }, [load, loadClaims]);

  const handleForceRelease = async (assessmentId) => {
    const reason = window.prompt(
      'Force-release this mentor\'s claim?\n\nReason (required for audit log):'
    );
    if (!reason || !reason.trim()) return;
    setReleasingId(assessmentId);
    try {
      await forceReleaseClaim({ assessmentId, reason: reason.trim() });
      setSnack({ open: true, message: 'Claim released back to the pool.', severity: 'success' });
      loadClaims();
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Release failed',
        severity: 'error',
      });
    } finally {
      setReleasingId(null);
    }
  };

  const resetForm = () => {
    setEmail('');
    setName('');
    setSpecialization('');
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await inviteMentor({
        email: email.trim(),
        name: name.trim(),
        specialization: specialization.trim(),
      });
      setSnack({
        open: true,
        message: `Invitation sent to ${email.trim()}. Cognito emails them a temporary password.`,
        severity: 'success',
      });
      setInviteOpen(false);
      resetForm();
      load();
    } catch (err) {
      setSnack({
        open: true,
        message: err?.response?.data?.error || err.message || 'Failed to invite mentor',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.name || params.row.given_name || '-',
    },
    {
      field: 'specialization',
      headerName: 'Specialization',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.specialization || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const status = params.row.status || params.row.user_status || 'UNKNOWN';
        const isActive = status === 'CONFIRMED' || status === 'active';
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              backgroundColor: isActive ? '#16a34a18' : '#6b728018',
              color: isActive ? '#16a34a' : '#6b7280',
              fontWeight: 600,
              border: `1px solid ${isActive ? '#16a34a40' : '#6b728040'}`,
            }}
          />
        );
      },
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 100,
      renderCell: (params) => {
        const enabled = params.row.enabled !== false;
        return (
          <Chip
            label={enabled ? 'Yes' : 'No'}
            size="small"
            color={enabled ? 'success' : 'default'}
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 170,
      valueGetter: (params) => params.row.created_at || params.row.user_create_date || params.row.created,
      valueFormatter: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Mentors</Typography>
          <Typography variant="body2" color="text.secondary">
            CISSP-certified reviewers who approve intern-submitted assessment reports before delivery.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteOpen(true)}
        >
          Invite Mentor
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Live claims panel — only shown if there are any */}
      {!claimsLoading && claims.length > 0 && (
        <Box sx={{ backgroundColor: 'background.paper', borderRadius: 1, p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon fontSize="small" />
            Active Claims ({claims.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mentors currently reviewing an assessment. SLA is 24 hours from claim — auto-released after.
          </Typography>
          <Stack spacing={1}>
            {claims.map((c) => {
              const claimedAt = c.claimed_at ? new Date(c.claimed_at * 1000) : null;
              const slaExpiresAt = c.sla_expires_at ? new Date(c.sla_expires_at * 1000) : null;
              const slaMs = slaExpiresAt ? slaExpiresAt.getTime() - Date.now() : null;
              const slaHoursLeft = slaMs != null ? slaMs / 3600000 : null;
              const critical = slaHoursLeft != null && slaHoursLeft < 4;
              return (
                <Box
                  key={c.assessment_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: critical ? 'error.main' : 'divider',
                    backgroundColor: critical ? '#fff5f5' : undefined,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {c.assessment_id?.slice(0, 8)}…
                      {c.service_type && (
                        <Chip label={c.service_type} size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mentor: {c.expert_id?.slice(0, 8) || '—'}… · Intern: {c.intern_id?.slice(0, 8) || '—'}…
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 140 }}>
                    <Typography variant="caption" color="text.secondary">Claimed</Typography>
                    <Typography variant="body2">{claimedAt ? claimedAt.toLocaleString() : '—'}</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', minWidth: 130 }}>
                    <Typography variant="caption" color={critical ? 'error' : 'text.secondary'}>
                      SLA
                    </Typography>
                    <Typography variant="body2" sx={{ color: critical ? 'error.main' : 'text.primary', fontWeight: critical ? 600 : 400 }}>
                      {slaHoursLeft != null
                        ? slaHoursLeft > 0
                          ? `${slaHoursLeft.toFixed(1)}h left`
                          : 'EXPIRED'
                        : '—'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<LockOpenIcon />}
                    onClick={() => handleForceRelease(c.assessment_id)}
                    disabled={releasingId === c.assessment_id}
                  >
                    {releasingId === c.assessment_id ? <CircularProgress size={16} /> : 'Force release'}
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {experts.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No mentors yet. Use "Invite Mentor" to add one.</Typography>
            </Paper>
          )}
          {experts.map((m) => {
            const status = m.status || m.user_status || 'UNKNOWN';
            const enabled = m.enabled !== false;
            return (
              <Paper key={m.id || m.email || m.sub || m.username || Math.random()} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                    {m.name || m.given_name || m.email || '—'}
                  </Typography>
                  <Chip label={status} size="small" color={status === 'CONFIRMED' || status === 'active' ? 'success' : 'default'} />
                </Box>
                <Typography variant="body2" color="text.secondary" noWrap>{m.email}</Typography>
                {m.specialization && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Specialization: {m.specialization}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {enabled ? 'Enabled' : 'Disabled'} · Created {formatDate(m.created_at || m.user_create_date || m.created)}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ height: 600, backgroundColor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={experts}
            columns={columns}
            getRowId={(row) => row.id || row.email || row.sub || row.username || Math.random()}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
          />
        </Box>
      )}

      <Dialog open={inviteOpen} onClose={() => { setInviteOpen(false); resetForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Invite a Mentor</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Cognito sends a welcome email with a temporary password. The mentor signs in at
            {' '}<b>mentors.cyberwisecorp.com</b>{' '}
            and is prompted to set a permanent password on first login.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Specialization (optional)"
              value={specialization}
              onChange={(e) => setSpecialization(e.target.value)}
              fullWidth
              placeholder="e.g. Cloud Security, SOC, Incident Response"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setInviteOpen(false); resetForm(); }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={!email.trim() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
